import React from 'react';
import { useFormContext } from 'react-hook-form';
import { ClientAddress } from '@/app/(main)/client-detail/edit-main/components/ClientAdress';
import { BILLING_FIELDS, MAILING_TO_BILLING_MAP } from '@/app/(main)/clients/create/const/passenger';
import type { CreateClientFormData } from '@/types/clientsApiTypes';

type MailingField = keyof typeof MAILING_TO_BILLING_MAP;

interface PrimaryBillingAddressProps {
  onUpdateFormData?: (fieldName: string, fieldValue: unknown) => void;
}

export const PrimaryBillingAddress = ({ onUpdateFormData }: PrimaryBillingAddressProps) => {
  const basePath = `primary_passenger.client` as const;
  const { watch, setValue, getValues } = useFormContext<CreateClientFormData>();

  const sameAsMailing = watch(`primary_passenger.client.is_billing_same_as_mailing`);

  const handleSetBillingFromMailing = () => {
    const mailing = getValues('primary_passenger.client');
    const billingPayload: Record<string, string> = {};

    (Object.keys(MAILING_TO_BILLING_MAP) as MailingField[]).forEach((mailingField: MailingField) => {
      const billingField = MAILING_TO_BILLING_MAP[mailingField];
      billingPayload[billingField] = mailing?.[mailingField] ?? '';

      setValue(`primary_passenger.client.${billingField}`, mailing?.[mailingField] ?? '', {
        shouldDirty: true,
      });
    });

    onUpdateFormData?.(basePath, billingPayload);
  };

  const onEmptyBillingAddress = () => {
    const billingPayload: Record<string, string> = {};
    BILLING_FIELDS.forEach((field) => {
      setValue(`${basePath}.${field}`, '');
      billingPayload[field] = '';
    });
    onUpdateFormData?.(basePath, billingPayload);
  };

  const handleChangeSameAsPrimary = (checked: boolean) => {
    setValue(`${basePath}.is_billing_same_as_mailing`, checked);
    onUpdateFormData?.(`${basePath}.is_billing_same_as_mailing`, checked);

    if (checked) {
      handleSetBillingFromMailing();
    } else {
      onEmptyBillingAddress();
    }
  };

  const selectPlaceAddress = (address: { [key: string]: string }) => {
    const cleanedAddress: { [key: string]: string } = {};
    Object.entries(address).forEach(([key, value]) => {
      const parts = key.split('.');
      const lastPart = parts[parts.length - 1];
      cleanedAddress[lastPart] = value;
    });
    onUpdateFormData?.(basePath, cleanedAddress);
  };

  const handleSaveOnBlur = (name: string, value: unknown) => {
    onUpdateFormData?.(name, value);
  };

  return (
    <div className="mt-4">
      <ClientAddress
        intent="primaryWhite"
        selectPlaceAddress={selectPlaceAddress}
        onBlur={(e) => handleSaveOnBlur(e.target.name, e.target.value)}
        toggleConfig={{
          label: 'Same as Mailing Address',
          visible: true,
          value: !!sameAsMailing,
          onChange: () => handleChangeSameAsPrimary(!sameAsMailing),
        }}
        fields={{
          address_line1: {
            label: 'Address Line 1',
            placeholder: 'Street address, P.O. box',
            required: false,
            name: `${basePath}.client_billing_address`,
          },
          address_line2: {
            label: 'Address Line 2',
            placeholder: 'Apartment, suite, unit, building',
            name: `${basePath}.client_billing_address_line_2`,
          },
          city: {
            label: 'City',
            placeholder: 'Enter city',
            required: false,
            name: `${basePath}.client_billing_city`,
          },
          state: {
            label: 'State/Province',
            placeholder: 'Select state or province',
            required: false,
            name: `${basePath}.client_billing_state`,
          },
          zip: {
            label: 'Zip Code',
            placeholder: 'Enter ZIP or postal code',
            required: false,
            name: `${basePath}.billing_zip_code`,
          },
          country: {
            label: 'Country',
            placeholder: 'Select country',
            required: false,
            name: `${basePath}.client_billing_country`,
          },
        }}
      />
    </div>
  );
};
